"use client";

import { Fragment, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircleQuestion } from "lucide-react";
import ApoBankCta from "@/components/shared/ApoBankCta";
import ArticleHtml from "@/components/blog/ArticleHtml";
import CTASection from "@/components/blog/CTASection";
import PrevNextNav from "@/components/blog/PrevNextNav";
import ShareButtons from "@/components/blog/ShareButtons";
import FAQAccordion from "@/components/unis/FAQAccordion";
import { articlePath, type BlogArticle, type BlogCardData } from "@/lib/blog";
import { CTA_VARIANTS, ctaForIndex } from "@/lib/cta-variants";
import { viewportOnce } from "@/lib/motion";

/** Artikelrumpf: Einleitung, Abschnitte, FAQ, Teilen (mobil), Vor/Zurück. */
export default function ArticleContent({
  article,
  prev,
  next,
}: {
  article: Pick<
    BlogArticle,
    "slug" | "category" | "title" | "introHtml" | "sections" | "faqs" | "hideCtas" | "apoBankCta"
  >;
  prev?: BlogCardData;
  next?: BlogCardData;
}) {
  // Zufälliger Startpunkt der CTA-Rotation. Erst nach dem Mount gesetzt, damit
  // Server- und Client-Erstrender identisch bleiben (kein Hydration-Mismatch);
  // die einheitliche Mindesthöhe der CTAs verhindert dabei jeden Layout-Shift.
  const [ctaStart, setCtaStart] = useState(0);
  useEffect(() => {
    setCtaStart(Math.floor(Math.random() * CTA_VARIANTS.length));
  }, []);

  return (
    <article className="col-span-12 lg:col-span-8 order-1 flex flex-col gap-12">
      {/* Einleitung als Lede */}
      {/* CSS entrance (.enter), so the lede is visible before hydration */}
      <div className="enter [--enter-y:16px] [--enter-delay:150ms] [animation-duration:0.55s] border-b border-line pb-10">
        <ArticleHtml html={article.introHtml} className="article-intro" />
      </div>

      {/* Abschnitte – nach jedem 3. Abschnitt ein CTA-Block. Zufälliger Start,
          danach rotieren die vier Varianten (0 → 1 → 2 → 3 → 0 …). */}
      {(() => {
        let ctaPos = 0;
        return article.sections.filter((section) => !section.hidden).map((section, i) => {
          const showCta = !article.hideCtas && (i + 1) % 3 === 0;
          const variant = showCta ? ctaForIndex(ctaStart + ctaPos++) : null;
          return (
            <Fragment key={section.id}>
              {/* Zusätzlicher apoBank-Anzeigenblock vor dem 2. Abschnitt –
                  gleiche Scroll-Animation wie die bestehenden CTA-Blöcke. */}
              {i === 1 && article.apoBankCta && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={viewportOnce}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <ApoBankCta variant={article.apoBankCta} />
                </motion.div>
              )}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                aria-labelledby={`${section.id}-heading`}
                className="flex flex-col gap-5"
              >
                <h2
                  id={section.id}
                  className="relative pb-4 font-display font-bold text-2xl sm:text-[1.7rem] tracking-tight leading-snug text-ink scroll-mt-28 after:content-[''] after:absolute after:left-0 after:bottom-0 after:block after:w-10 after:h-1 after:rounded-full after:bg-accent"
                >
                  <span id={`${section.id}-heading`}>{section.heading}</span>
                </h2>
                <ArticleHtml html={section.html} />
              </motion.section>
              {variant && (
                <CTASection
                  eyebrow={variant.eyebrow}
                  heading={variant.heading}
                  description={variant.description}
                  buttonLabel={variant.buttonLabel}
                  buttonHref={variant.buttonHref}
                  appPopup={variant.appPopup ?? false}
                  backgroundImage={variant.backgroundImage}
                />
              )}
            </Fragment>
          );
        });
      })()}

      {/* Versteckte Abschnitte (z. B. „Quellen"): nicht sichtbar, aber im DOM,
          damit Crawler/Answer-Engines die Belege weiterhin lesen können.
          Bewusst sr-only statt display:none; kein TOC-Eintrag, kein CTA-Slot. */}
      {article.sections
        .filter((section) => section.hidden)
        .map((section) => (
          <section key={section.id} aria-labelledby={`${section.id}-heading`} className="sr-only">
            <h2 id={`${section.id}-heading`}>{section.heading}</h2>
            <div dangerouslySetInnerHTML={{ __html: section.html }} />
          </section>
        ))}

      {/* FAQ */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        aria-labelledby="article-faq-heading"
        className="card-flat bg-white/70 p-6 sm:p-8"
      >
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-accent mb-2">
          <MessageCircleQuestion size={14} aria-hidden="true" />
          FAQ
        </p>
        <h2
          id="article-faq-heading"
          className="font-display font-bold text-2xl tracking-tight text-ink mb-4"
        >
          Häufige Fragen
        </h2>
        <FAQAccordion faqs={article.faqs} />
      </motion.section>

      {/* Teilen (mobil) + Navigation */}
      <div className="lg:hidden">
        <ShareButtons title={article.title} path={articlePath(article)} />
      </div>
      <PrevNextNav prev={prev} next={next} />
    </article>
  );
}
